import {
  ORGANIZATION_PERMISSION_GROUPS,
  ORGANIZATION_ROLE_LABELS,
} from "~/types/organizations";
import type {
  OrganizationMemberResponse,
  OrganizationMemberCapabilities,
  OrganizationInvitationResponse,
  OrganizationInvitationCapabilities,
  OrganizationPermissionKey,
  OrganizationRole,
  OrganizationSummaryResponse,
} from "~/types/organizations";

export function organizationRoleName(
  role: OrganizationRole,
  name?: string,
): string {
  return name || ORGANIZATION_ROLE_LABELS[role] || role;
}

export function hasOrganizationPermission(
  organization:
    | Pick<OrganizationSummaryResponse, "role" | "permissions">
    | null
    | undefined,
  permission: OrganizationPermissionKey,
): boolean {
  return Boolean(
    organization &&
    (organization.role === "owner" ||
      organization.permissions?.[permission] === true),
  );
}

// All school roles share one catalog. The backend evaluates every grant.
export function getOrganizationPermissionGroups(_role?: OrganizationRole) {
  return ORGANIZATION_PERMISSION_GROUPS;
}

export function filterOrganizationPermissionsForRole(
  _role: OrganizationRole,
  permissions: Record<string, boolean | null | undefined>,
): Record<string, boolean> {
  const keys = new Set(
    ORGANIZATION_PERMISSION_GROUPS.flatMap((group) =>
      group.items.map((item) => item.key),
    ),
  );
  return Object.fromEntries(
    Object.entries(permissions).filter(
      (entry): entry is [string, boolean] =>
        keys.has(entry[0] as OrganizationPermissionKey) &&
        typeof entry[1] === "boolean",
    ),
  );
}

/** Shared by the menu and direct-URL guard; ownership stays on the API. */
export function canAccessSchoolDashboardRoute(
  organization: OrganizationSummaryResponse | null | undefined,
  path: string,
  documentAccess = false,
): boolean {
  if (!hasOrganizationPermission(organization, "organization.dashboard.access"))
    return false;
  const has = (key: OrganizationPermissionKey) =>
    hasOrganizationPermission(organization, key);
  const section = path.split("/")[2] || "";
  if (section === "permissions")
    return organization?.capabilities?.can_manage_roles === true;
  if (section === "notifications")
    return [
      "organization.notifications.view",
      "organization.communications.inbox.view",
      "organization.communications.manage",
      "organization.communications.send",
      "organization.communications.configure",
    ].some((permission) => has(permission as OrganizationPermissionKey));
  if (section === "documents") return documentAccess && has("documents.manage");
  if (
    ["entries", "requests"].includes(section) ||
    /^\/school-dashboard\/students\/[^/]+\/entry\/?$/.test(path)
  )
    return (
      has("students.manage") || (documentAccess && has("documents.manage"))
    );
  const permissions: Record<string, OrganizationPermissionKey> = {
    team: "organization.members.view",
    students: "students.view",
    interests: "interests.view",
    forms: "forms.view",
    classrooms: "classrooms.view",
    attendance: "attendance.view",
    finance: "finance.view",
    receiving: "finance.view",
    bookings: "scheduling.view",
    assessments: "assessments.view",
    reports: "organization.reports.view",
    billing: "organization.billing.view",
    preferences: "organization.preferences.view",
    integrations: "organization.communications.configure",
    notifications: "organization.notifications.view",
  };
  if (section === "materials")
    return (
      has("content.school_materials.view") ||
      has("content.school_materials.manage")
    );
  if (section === "store") {
    const storeSection = path.split("/")[3] || "";
    if (storeSection === "orders") return has("store.orders.view");
    if (storeSection === "settings")
      return has("store.settings.manage") || has("store.payments.manage");
    return has("store.catalog.view");
  }
  return permissions[section] ? has(permissions[section]) : true;
}

export function canManageOrganizationMemberAccess(
  organization: OrganizationSummaryResponse | null | undefined,
): boolean {
  return (
    organization?.capabilities?.can_manage_member_access ??
    organization?.role === "owner"
  );
}

/** Row capabilities are authoritative; the fallback supports older profile payloads. */
export function getOrganizationMemberCapabilities(
  organization: OrganizationSummaryResponse | null | undefined,
  member: Pick<OrganizationMemberResponse, "role" | "capabilities">,
): OrganizationMemberCapabilities {
  const manageAccess = canManageOrganizationMemberAccess(organization);
  const manageMembers = hasOrganizationPermission(
    organization,
    "organization.members.manage",
  );
  return {
    can_update:
      member.capabilities?.can_update ??
      (manageMembers && (member.role !== "owner" || manageAccess)),
    can_change_role:
      manageAccess && (member.capabilities?.can_change_role ?? true),
    can_change_permissions:
      member.role !== "owner" &&
      manageAccess &&
      (member.capabilities?.can_change_permissions ?? true),
  };
}

export function getOrganizationInvitationCapabilities(
  organization: OrganizationSummaryResponse | null | undefined,
  invitation: Pick<
    OrganizationInvitationResponse,
    "role" | "status" | "is_expired" | "capabilities"
  >,
): OrganizationInvitationCapabilities {
  const targetAllowed =
    invitation.role !== "owner" ||
    canManageOrganizationMemberAccess(organization);
  const manageMembers =
    hasOrganizationPermission(organization, "organization.members.manage") &&
    targetAllowed;
  return {
    can_resend:
      targetAllowed &&
      (invitation.capabilities?.can_resend ??
        (manageMembers && ["invited", "expired"].includes(invitation.status))),
    can_cancel:
      targetAllowed &&
      (invitation.capabilities?.can_cancel ??
        (manageMembers &&
          invitation.status === "invited" &&
          !invitation.is_expired)),
  };
}
